import { useMemo } from "react";
import { AccommodationAPIPromiseClient } from "../../../../gen/witbooking/backoffice/accommodation/v1/accommodation_api_grpc_web_pb";
import {
  Accommodation,
  CategoryLimitsInput,
  CreateAccommodationRequest,
  DeleteAccommodationRequest,
  GetAccommodationRequest,
  GetAccommodationsRequest,
  ReorderAccommodationRequest,
  UpdateAccommodationRequest,
} from "../../../../gen/witbooking/backoffice/accommodation/v1/accommodation_api_pb";
import {
  useGrpcClient,
  usePropertyGrpcCall,
  usePropertyScopedGrpcCall,
} from "../../../../lib/grpc";
import { ValidationIssue } from "../../../extrasCategories/domain/entities/ValidationIssue";
import { ValidationIssueMapper } from "../../../extrasCategories/infrastructure/mappers/ValidationIssueMapper";

export type AccommodationObject = Accommodation.AsObject;

/** Non-blocking issues of a successful save. */
export interface SaveAccommodationResult {
  warnings: ValidationIssue[];
}

export function useAccommodationApi() {
  const grpcClient = useGrpcClient(AccommodationAPIPromiseClient);
  const getAccommodationsCall = usePropertyScopedGrpcCall(grpcClient, grpcClient.getAccommodations);
  const getAccommodation = usePropertyGrpcCall(grpcClient, grpcClient.getAccommodation);
  const createAccommodation = usePropertyGrpcCall(grpcClient, grpcClient.createAccommodation);
  const updateAccommodation = usePropertyGrpcCall(grpcClient, grpcClient.updateAccommodation);
  const deleteAccommodation = usePropertyGrpcCall(grpcClient, grpcClient.deleteAccommodation);
  const reorderAccommodation = usePropertyGrpcCall(grpcClient, grpcClient.reorderAccommodation);

  return useMemo(
    () => ({
      getAccommodations: async (propertyTicker?: string) => {
        const response = await getAccommodationsCall(
          propertyTicker,
          new GetAccommodationsRequest()
        );
        return response.getAccommodationList().map(it => it.toObject());
      },
      getAccommodation: async (accommodationId: string) => {
        const request = new GetAccommodationRequest();
        request.setAccommodationId(accommodationId);
        const response = await getAccommodation(request);
        const accommodation = response.getAccommodation();
        if (!accommodation) {
          throw new Error("Unexpected reply obtaining accommodation");
        }
        return accommodation.toObject();
      },
      createAccommodation: async (
        accommodation: Accommodation,
        categoryLimits?: CategoryLimitsInput
      ): Promise<SaveAccommodationResult> => {
        const request = new CreateAccommodationRequest();
        request.setAccommodation(accommodation);
        request.setCategoryLimits(categoryLimits);
        const response = await createAccommodation(request);
        return { warnings: ValidationIssueMapper.toDomainList(response.getWarningsList()) };
      },
      updateAccommodation: async (
        accommodation: Accommodation,
        categoryLimits?: CategoryLimitsInput
      ): Promise<SaveAccommodationResult> => {
        const request = new UpdateAccommodationRequest();
        request.setAccommodation(accommodation);
        request.setCategoryLimits(categoryLimits);
        const response = await updateAccommodation(request);
        return { warnings: ValidationIssueMapper.toDomainList(response.getWarningsList()) };
      },
      deleteAccommodation: async (accommodationId: string) => {
        const request = new DeleteAccommodationRequest();
        request.setAccommodationId(accommodationId);
        await deleteAccommodation(request);
      },
      reorderAccommodation: async (accommodationId: string, afterId: string) => {
        const request = new ReorderAccommodationRequest();
        request.setAccommodationId(accommodationId);
        request.setAfterId(afterId);
        await reorderAccommodation(request);
      },
    }),
    [
      createAccommodation,
      deleteAccommodation,
      getAccommodation,
      getAccommodationsCall,
      reorderAccommodation,
      updateAccommodation,
    ]
  );
}
